import { useMutation, useQueryClient } from "react-query";
import { RestError } from "../../services/rest-service/RestService";
import { IUIUserFavoriteModel } from "../../services/ui-personalization-data-service/Models";
import UIPersonalizationDataService from "../../services/ui-personalization-data-service/UIPersonalizationDataService";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";
import { FavoriteMutationContextType } from "./Common";
import { keys } from "./Keys";

export type UseSaveUserFavoriteResultType = {
    savedDescription: string;
};

export type UseSaveUserFavoriteParameters = {
} & BaseUseMutationParameters<UseSaveUserFavoriteResultType, IUIUserFavoriteModel>;

export function useSaveUserFavorite({
    onError,
    onSuccess
}: UseSaveUserFavoriteParameters) {
    const queryClient = useQueryClient();

    return useMutation<UseSaveUserFavoriteResultType, RestError, IUIUserFavoriteModel, FavoriteMutationContextType>(
        async (favorite) => {
            await UIPersonalizationDataService.saveUserFavorite(favorite);

            return {
                savedDescription: favorite.description
            };
        },
        {
            onMutate: async (favorite: IUIUserFavoriteModel) => {

                await queryClient.cancelQueries(keys.all);

                const previousFavorites = queryClient.getQueryData<Array<IUIUserFavoriteModel>>(
                    keys.favoriteList()
                );

                queryClient.setQueryData<Array<IUIUserFavoriteModel>>(
                    keys.favoriteList(),
                    (favorites: Array<IUIUserFavoriteModel> | undefined) => {

                        const existingFavorite = favorites?.find(f => f.id === favorite.id);

                        return existingFavorite
                            ? favorites?.map(f => (f.id === favorite.id ? favorite : f)) ?? []
                            : [...(favorites ?? []), favorite];
                    }
                );

                return {
                    previousFavorites: previousFavorites
                };
            },
            onError: onError,
            onSuccess: onSuccess,
            onSettled: (_data, error, _favorite, context) => {

                if (error && context?.previousFavorites) {
                    queryClient.setQueryData<Array<IUIUserFavoriteModel>>(
                        keys.favoriteList(),
                        context?.previousFavorites
                    );
                }

                if (!error) {
                    queryClient.invalidateQueries(keys.favoriteList());
                }
            }
        }
    );
}
